import { type Chat, type ChatMessage, type Content, previewOf } from "@rhema/client";
import { formatOf, plainOf } from "../conversation/rich";
import { clockTime } from "../data/view";

/** How many recent messages Queravel reads in a chat. */
export const ASK_RECENT = 30;

export const LANGUAGES = [
  "English",
  "French",
  "Spanish",
  "Portuguese",
  "German",
  "Arabic",
  "Chinese",
  "Swahili",
  "Hausa",
  "Igbo",
  "Yoruba",
  "Twi",
];

export type Task = "summarise" | "reply" | "translate" | "tasks" | "question";
export type AskRequest = { task: Task; language?: string; question?: string };

const SENDABLE = new Set<Content["type"]>(["text", "media", "location", "contact", "poll"]);

/**
 * Which of these messages may go to Queravel. Messages that disappear are never sent, and neither
 * are deleted ones, notices or anything this device could not read.
 */
export function sendableMessages(messages: ChatMessage[]) {
  const readable = messages.filter(
    (m) => m.kind === "message" && !m.deleted && m.content && SENDABLE.has(m.content.type),
  );
  const sendable = readable.filter((m) => !m.expiresAt);
  return { sendable, leftOut: readable.length - sendable.length };
}

/** The recent messages of a chat that Queravel reads when nothing more particular was picked. */
export function recentMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.filter((m) => m.kind === "message" && m.seq > 0).slice(-ASK_RECENT);
}

export function nameOf(chat: Chat, id: string | null, meId?: string): string {
  if (id && id === meId) return "Me";
  return chat.members.find((m) => m.user.id === id)?.user.display_name ?? "Someone";
}

export function lineOf(m: ChatMessage): string {
  const c = m.content;
  // Queravel reads the words. Formatting marks are left out and link addresses kept.
  if (c?.type === "text") return plainOf(m.edited ?? c.body, formatOf(m), true);
  if (c?.type === "media") {
    const caption = plainOf(m.edited ?? c.caption ?? "", formatOf(m), true);
    return caption ? `(${c.kind}) ${caption}` : `(${c.kind})`;
  }
  return previewOf(c ?? null);
}

export function instruction(req: AskRequest): string {
  switch (req.task) {
    case "summarise":
      return "Summarise these messages in a few short lines.";
    case "reply":
      return "Draft a reply I could send next in this chat. Write only the reply, in my own voice.";
    case "translate":
      return `Translate these messages into ${req.language ?? "English"}.`;
    case "tasks":
      return "List the tasks, dates and times in these messages. Say who each one is for when the messages say so.";
    case "question":
      return req.question ?? "";
  }
}

export function count(n: number): string {
  return n === 1 ? "1 message" : `${n} messages`;
}

/** The short line kept in place of the whole request. Without messages it names only the chat. */
export function labelFor(req: AskRequest, n: number, title: string): string {
  const some = n > 0 ? `${count(n)} from ${title}` : title;
  switch (req.task) {
    case "summarise":
      return `Summarise ${some}`;
    case "reply":
      return `Draft a reply in ${title}`;
    case "translate":
      return `Translate ${some} into ${req.language ?? "English"}`;
    case "tasks":
      return `List tasks and dates in ${some}`;
    case "question":
      return n > 0 ? `${req.question ?? ""} (about ${some})` : (req.question ?? "");
  }
}

/** The message that goes to Queravel: the request, then exactly the messages shown to the person. */
export function promptFor(
  req: AskRequest,
  chat: Chat,
  title: string,
  messages: ChatMessage[],
  meId?: string,
): string {
  if (!messages.length) return instruction(req);
  const lines = messages.map(
    (m) => `[${nameOf(chat, m.senderId, meId)}, ${clockTime(m.createdAt)}] ${lineOf(m)}`,
  );
  return `${instruction(req)}\n\nMessages from ${title}:\n${lines.join("\n")}`;
}
